import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Form, Alert, Badge, ListGroup, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../api';

// Espace admin : gestion des demandes d'onboarding (valider / refuser) et
// invitation proactive de membres. Endpoints : backend/admin/onboarding_*.php.
const OnboardingAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Invitation
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inviteMember, setInviteMember] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [inviteErr, setInviteErr] = useState('');

  const loadSubmissions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/backend/admin/onboarding_list.php', { credentials: 'include' });
      setSubmissions(data.submissions || []);
    } catch (e) {
      setError('Erreur lors du chargement des demandes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSubmissions(); }, []);

  // Garde admin APRÈS tous les hooks (sinon ordre des hooks incohérent -> crash)
  if (!user || user.role !== 'admin') {
    return <Navigate to="/members-area" replace />;
  }

  const act = async (endpoint, id) => {
    setNotice('');
    setError('');
    try {
      const data = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      });
      if (data.success) {
        setNotice(data.message || 'Action effectuée.');
        loadSubmissions();
      } else {
        setError(data.message || 'Action impossible.');
      }
    } catch (e) {
      setError('Erreur serveur.');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setInviteErr('');
    setInviteMsg('');
    setResults([]);
    if (query.trim().length < 2) { setInviteErr('Entrez au moins 2 lettres.'); return; }
    setSearching(true);
    try {
      const data = await apiFetch(`/backend/onboarding/search_members.php?q=${encodeURIComponent(query.trim())}`, { credentials: 'include' });
      setResults(data.members || []);
      if ((data.members || []).length === 0) setInviteErr('Aucun membre non-activé trouvé.');
    } catch (e) {
      setInviteErr('Erreur lors de la recherche.');
    } finally {
      setSearching(false);
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviteErr('');
    setInviteMsg('');
    if (!inviteMember || !inviteEmail) { setInviteErr('Sélectionnez un membre et saisissez un email.'); return; }
    try {
      const data = await apiFetch('/backend/admin/onboarding_invite.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: inviteMember.id, email: inviteEmail }),
      });
      if (data.success) {
        setInviteMsg(data.message || 'Invitation envoyée.');
        setInviteMember(null);
        setInviteEmail('');
        setQuery('');
        setResults([]);
      } else {
        setInviteErr(data.message || 'Envoi impossible.');
      }
    } catch (e) {
      setInviteErr('Erreur serveur.');
    }
  };

  return (
    <Container className="mt-5 pb-5">
      <div className="mt-5" />
      <div className="d-flex justify-content-between align-items-center">
        <h2>Onboarding des membres</h2>
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin')}>← Actualités</Button>
      </div>

      {notice && <Alert variant="success" className="mt-3">{notice}</Alert>}
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

      {/* Demandes en attente */}
      <h4 className="mt-4">Demandes en attente de validation</h4>
      {loading ? (
        <div className="my-3"><Spinner animation="border" size="sm" /> Chargement…</div>
      ) : submissions.length === 0 ? (
        <p className="text-muted">Aucune demande en attente.</p>
      ) : (
        submissions.map((s) => (
          <Card key={s.id} className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <Card.Title className="mb-1">
                    {s.first_name} {s.name}{' '}
                    <Badge bg={s.source === 'invitation' ? 'info' : 'secondary'}>{s.source}</Badge>
                  </Card.Title>
                  <div className="text-muted small">
                    Email demandé : <strong>{s.login_email}</strong>
                    {s.current_email ? <> · actuel : {s.current_email}</> : null}
                  </div>
                  {s.contact_email && <div className="small">Contact public : {s.contact_email}</div>}
                  {s.website && <div className="small">Site : {s.website}</div>}
                  {s.biography && <div className="small mt-1"><em>{s.biography.slice(0, 200)}</em></div>}
                  {s.profile_picture && (
                    <div className="mt-2">
                      <img src={s.profile_picture} alt="" style={{ maxHeight: 80, borderRadius: 8 }} />
                    </div>
                  )}
                </div>
                <div className="d-flex flex-column gap-2">
                  <Button variant="success" size="sm" onClick={() => act('/backend/admin/onboarding_approve.php', s.id)}>
                    Valider
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => act('/backend/admin/onboarding_reject.php', s.id)}>
                    Refuser
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))
      )}

      {/* Invitation proactive */}
      <h4 className="mt-5">Inviter un membre</h4>
      <p className="text-muted">
        Le membre invité recevra un lien ; son profil sera activé automatiquement à la fin du formulaire (pas de validation supplémentaire).
      </p>
      {inviteMsg && <Alert variant="success">{inviteMsg}</Alert>}
      {inviteErr && <Alert variant="danger">{inviteErr}</Alert>}

      <Form onSubmit={handleSearch} className="mb-2">
        <Row className="g-2">
          <Col><Form.Control placeholder="Nom du membre" value={query} onChange={(e) => setQuery(e.target.value)} /></Col>
          <Col xs="auto">
            <Button type="submit" variant="outline-primary" disabled={searching}>
              {searching ? <Spinner size="sm" animation="border" /> : 'Rechercher'}
            </Button>
          </Col>
        </Row>
      </Form>

      {results.length > 0 && (
        <ListGroup className="mb-3">
          {results.map((m) => (
            <ListGroup.Item key={m.id} action active={inviteMember && inviteMember.id === m.id} onClick={() => setInviteMember(m)}>
              {m.first_name} {m.name}{m.zipCity ? <span className="text-muted"> — {m.zipCity}</span> : null}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {inviteMember && (
        <Form onSubmit={sendInvite}>
          <Form.Group className="mb-2">
            <Form.Label>Email d'invitation pour {inviteMember.first_name} {inviteMember.name}</Form.Label>
            <Form.Control type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required />
          </Form.Group>
          <Button type="submit" variant="primary">Envoyer l'invitation</Button>
        </Form>
      )}
    </Container>
  );
};

export default OnboardingAdmin;
